import { Input } from "@d12labs/ui/input";
import { cn } from "@d12labs/utils";
import { Search } from "lucide-react";
import { sectionIcon } from "./icons";

export type NavItem = { id: string; title: string; icon?: string };

/**
 * The sticky left rail: a filter box, the section list (active item driven by
 * scroll-spy), and a legend explaining the `env` chip. Fills the column that the
 * old layout left empty.
 */
export function SettingsNav({
	items,
	activeId,
	query,
	onQueryChange,
	onJump,
}: {
	items: NavItem[];
	activeId: string;
	query: string;
	onQueryChange: (q: string) => void;
	onJump: (id: string) => void;
}) {
	return (
		<nav className="flex flex-col overflow-y-auto border-r border-hairline bg-background">
			<div className="p-3">
				<div className="relative">
					<Search className="pointer-events-none absolute left-2.5 top-1/2 size-3.5 -translate-y-1/2 text-ink-tertiary" />
					<Input
						value={query}
						onChange={(e) => onQueryChange(e.target.value)}
						placeholder="Filter settings…"
						className="h-8 pl-8 text-body-sm"
					/>
				</div>
			</div>

			<div className="px-2 pb-2">
				<div className="px-2 pb-1.5 pt-2 text-eyebrow uppercase text-ink-tertiary">Sections</div>
				{items.map((n) => {
					const Icon = sectionIcon(n.icon);
					const active = n.id === activeId;
					return (
						<button
							key={n.id}
							type="button"
							onClick={() => onJump(n.id)}
							className={cn(
								"flex h-8 w-full items-center gap-2.5 rounded-md px-2 text-body-sm transition-colors",
								active
									? "bg-surface-2 font-medium text-foreground"
									: "text-ink-muted hover:bg-surface-1 hover:text-foreground",
							)}
						>
							<Icon className="size-3.5 flex-none opacity-90" />
							<span className="flex-1 truncate text-left">{n.title}</span>
							{active ? <span className="size-1.5 flex-none rounded-full bg-primary" /> : null}
						</button>
					);
				})}
			</div>

			<div className="mt-auto border-t border-hairline p-3">
				<div className="flex items-start gap-2 rounded-md border border-hairline bg-surface-1 p-2.5">
					<span className="inline-flex size-5 flex-none items-center justify-center rounded border border-primary/30 bg-primary/10 text-[10px] font-medium text-primary-hover">
						env
					</span>
					<p className="text-meta leading-snug text-ink-subtle">
						Locked rows are pinned by an environment variable. Edit them in your{" "}
						<span className="font-mono text-ink-muted">.env</span> or host config, then restart.
					</p>
				</div>
			</div>
		</nav>
	);
}
